import { Logger } from '@deqo/foundation';
import { ApiClient } from '../api/ApiClient';
import { v2 } from '../api/ApiInterfaceV2';
import { UserMetricsData } from '../api/endpoints/internal';
import { LocalStorageService, StorageService } from '../platform/StorageService';
import { AppLocation } from './AppLocation';
import { ActiveObject, resolve } from './Container';
import { ProfileModel } from './ProfileModel';

const debug = Logger.get('app.telemetry');

export class MetricsHub extends ActiveObject {

    @resolve(ApiClient) private api: ApiClient;
    @resolve(ProfileModel) private me: ProfileModel;
    @resolve(AppLocation) private location: AppLocation;
    @resolve(LocalStorageService) private localData!: StorageService;

    private values: UserMetricsData = {};

    private get path() {
        return `deqo/m/${this.me.id}`;
    }

    public async start() {
        await this.flush();
        this.burden(
            () => this.location.observe((n, p) => {
                if (n !== p) this.flush();
            })
        );
    }

    public check(key: string) {
        return (this.values[key] ?? 0) > 0;
    }

    public get(key: string) {
        return this.values[key] || 0;
    }

    public set(key: string, value: number = 1) {
        this.values[key] = value;
        this.localData.update(this.path, x => (x ?? []).concat([['set', key, value]]));
        debug.log(`Metrics: set ${key} to ${this.values[key]}`);
    }

    public inc(key: string, value: number = 1) {
        this.values[key] = (this.values[key] || 0) + value;
        this.localData.update(this.path, x => (x ?? []).concat([['inc', key, value]]));
        debug.log(`Metrics: inc ${key} to ${this.values[key]}`);
    }

    public async flush() {
        const next = this.localData.delete(this.path);
        if (next) {
            await this.api.exec(v2.internal.metrics.update(next));
            debug.log('Metrics: flushed');
        }
        if (next || Object.keys(this.values).length === 0) {
            this.values = await this.api.exec(v2.internal.metrics.info());
            debug.log('Metrics: loaded ');
            debug.log(JSON.stringify(this.values, null, 4));
        }
        // Schedule a flush in 5 minutes
        const after = (1000 * 60 * 5) + Math.round(Math.random() * 1000 * 60);
        this.scheduler.timeout('flush', after, () => this.flush());
    }

}
